import React from 'react';
import styled from 'styled-components';
import { Menu, MenuButton, MenuSection, MenuHeading, MenuItem, MenuItems } from '@headlessui/react';
import { focusVisibleInsetStylesNoWrapper, standardTransitionStyles } from 'design-system/src/sharedStyles';
import { popoverPanelStyles, popoverPanelFocusOverrides } from '../Popover/shared';
import Hairline from '../Hairline/Hairline';

// These components don't require styling because they wrap
// some other element that's styled directly
export const PopoverMenuButton = MenuButton;
export const PopoverMenuItem = MenuItem;

const verticalPadding = 8;
const horizontalPadding = 12;

export const StyledMenuItems = styled(MenuItems)<{ $width?: string }>`
  ${popoverPanelStyles}
  width: ${({ $width }) => $width || '18.75rem'};
  padding: 4px;
  ${popoverPanelFocusOverrides}
`;

export const PopoverMenuSection = styled(MenuSection)`
  width: 100%;
`;

export const PopoverMenuHeading = styled(MenuHeading)`
  padding: ${verticalPadding}px ${horizontalPadding}px;
  color: var(--grayscale-400);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  // TODO: replace with Small Body/Regular typography component when it's available
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1rem;
  letter-spacing: 0.009375rem;
`;

export const PopoverMenuHairline = styled(Hairline)`
  margin: 0.5rem auto 0.75rem !important;
  width: calc(100% - ${horizontalPadding * 2}px) !important;
`;

export const PopoverMenuItemButton = styled.button`
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  color: var(--grayscale-900);
  background-color: transparent;
  border-radius: var(--border-radius-small);
  border: none;
  padding: ${verticalPadding}px ${horizontalPadding}px;
  ${standardTransitionStyles}
  &:hover,
  &:active {
    background: var(--grayscale-50);
  }
  // Data attribute applied by Headless UI when the item is focused
  &[data-headlessui-state~='active'] {
    ${focusVisibleInsetStylesNoWrapper}
  }
  // Prevent focus styles from applying when the item is hovered
  // This is needed because the focus state is applied when the item is hovered even if the user is not using a keyboard
  &[data-headlessui-state~='active']:hover {
    box-shadow: none;
    outline: none;
  }
`;

export type PopoverMenuItemsProps = { children: React.ReactNode; width?: string; anchor?: any };

export const PopoverMenuItems = (props: PopoverMenuItemsProps) => {
  const { children, width, anchor = 'top start', ...rest } = props;

  return (
    <StyledMenuItems anchor={anchor} transition={true} $width={width} {...rest}>
      {children}
    </StyledMenuItems>
  );
};

export type PopoverMenuProps = { children: React.ReactNode };

export const PopoverMenu = (props: PopoverMenuProps) => {
  const { children, ...rest } = props;

  return (
    <Menu {...rest}>
      {/* This div is required by Headless UI; the Menu children need to be wrapped */}
      <div>{children}</div>
    </Menu>
  );
};
